import styled from 'styled-components';
import { RelationshipsViewNodeIcon } from './RelationshipsViewNodeIcon';
import { colors } from '@ardoq/design-tokens';
import { ArdoqId } from '@ardoq/api-types';
import { LABEL_FONT_SIZE } from './consts';
import type { BrokenImageMouseEvents } from '@ardoq/icons';
import { RelationshipsNode } from './types';
import { getGraphNodeLabel, nodeRadius } from './util';
import { COMPONENT_ID_ATTRIBUTE } from '@ardoq/global-consts';
import { OnComponentClick, OnComponentDoubleClick } from '../types';

const Container = styled.g`
  text {
    line-height: 10px;
  }
  text,
  image,
  svg {
    pointer-events: none;
  }

  > .broken-image {
    /*Needed for broken image popover placement*/
    pointer-events: auto;
  }
`;

const Circle = styled.circle`
  stroke-width: 1px;
  &.workspace,
  &.component-stroke,
  &.fieldGraphNode,
  &.tagGraphNode {
    filter: none;
    fill: ${colors.grey300};
    stroke: ${colors.black950};
  }
  &.workspace {
    fill: ${colors.purple60};
  }
`;
const Label = styled.text`
  font-size: ${LABEL_FONT_SIZE}px;
  dominant-baseline: middle;
  text-anchor: middle;
  stroke: ${colors.white950};
  stroke-width: 3px;
  stroke-opacity: 0.8;
  paint-order: stroke;
`;

type RelationshipsViewNodeProperties = BrokenImageMouseEvents & {
  graphNode: RelationshipsNode;
  onComponentClick: OnComponentClick;
  onComponentDoubleClick: OnComponentDoubleClick;
  selectedComponentId?: ArdoqId | null;
};
const RelationshipsViewNode = ({
  graphNode,
  onComponentClick,
  onComponentDoubleClick,
  selectedComponentId,
  onBrokenImageMouseEnter,
  onBrokenImageMouseLeave,
}: RelationshipsViewNodeProperties) => {
  const radius = nodeRadius(graphNode, selectedComponentId);
  const contextMenuDataAttribute = {
    [COMPONENT_ID_ATTRIBUTE]: graphNode.modelId,
  };

  const { fullLabel, truncatedLabel, isLabelTruncated } =
    getGraphNodeLabel(graphNode);

  return (
    <Container key={graphNode.id} data-node-id={graphNode.id}>
      <Circle
        onClick={event => {
          if (!graphNode.isComponent()) return;
          onComponentClick(graphNode.modelId);
          event.stopPropagation();
        }}
        onDoubleClick={event => {
          if (!graphNode.isComponent()) return;
          onComponentDoubleClick(graphNode.modelId);
          event.stopPropagation();
        }}
        data-node-id={graphNode.id}
        className={`component-stroke ${graphNode.getCSS({
          useAsBackgroundStyle: true,
        })}`}
        data-tooltip-text={isLabelTruncated ? fullLabel : null}
        r={radius}
        {...contextMenuDataAttribute}
      />
      <RelationshipsViewNodeIcon
        graphNode={graphNode}
        nodeRadius={radius}
        onBrokenImageMouseEnter={onBrokenImageMouseEnter}
        onBrokenImageMouseLeave={onBrokenImageMouseLeave}
      />
      <Label y={radius + LABEL_FONT_SIZE}>{truncatedLabel}</Label>
    </Container>
  );
};
export { RelationshipsViewNode };
